import React, { useRef, useEffect, useState } from "react";
import { Stage, Layer, Rect, Text, Group, Image as KonvaImage, Arc } from "react-konva";

const GRID_SIZE = 28;

// Componente para el escenario
const EscenarioComponent = ({ escenario }) => {
  if (!escenario) return null;

  return (
    <Group x={escenario.x} y={escenario.y}>
      {escenario.tipo === "semicircular" ? (
        <>
          <Arc
            width={escenario.width}
            height={escenario.height}
            angle={180}
            rotation={180}
            fill="#2C3E50"
            stroke="#34495E"
            strokeWidth={3}
          />
          <Text
            text="ESCENARIO"
            fontSize={16}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
            width={escenario.width}
            x={0}
            y={escenario.height / 2 - 8}
          />
        </>
      ) : (
        <>
          <Rect
            width={escenario.width}
            height={escenario.height}
            fill="#2C3E50"
            stroke="#34495E"
            strokeWidth={3}
          />
          <Text
            text="ESCENARIO"
            fontSize={16}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
            width={escenario.width}
            height={escenario.height}
          />
        </>
      )}
    </Group>
  );
};

// Componente para un asiento
const AsientoComponent = ({ asiento, sector, isSelected, isVendido, onClick }) => {
  if (!sector || sector.visible === false) return null;

  let color = sector.color || "#5B9BD5";

  // Prioridad: vendido > seleccionado > reservado > bloqueado > disponible
  if (isVendido || asiento.estado === "vendido") {
    color = "#DC3545"; // Rojo
  } else if (isSelected) {
    color = "#28A745"; // Verde
  } else if (asiento.estado === "reservado") {
    color = "#FFA500"; // Naranja
  } else if (asiento.estado === "bloqueado") {
    color = "#6C757D"; // Gris
  }

  // Convertir color hex a rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handleClick = () => {
    // Solo permitir click si está disponible y no vendido
    if (asiento.estado === "disponible" && !isVendido) {
      onClick(asiento);
    }
  };

  // Opacidad mayor cuando está seleccionado
  const opacity = isSelected ? 0.7 : 0.3;

  return (
    <Group
      x={asiento.x}
      y={asiento.y}
      onClick={handleClick}
      onTap={handleClick}
      style={{ cursor: (asiento.estado === "disponible" && !isVendido) ? 'pointer' : 'not-allowed' }}
    >
      {/* Rectángulo del asiento */}
      <Rect
        width={asiento.width}
        height={asiento.height}
        fill={hexToRgba(color, opacity)}
        stroke={color}
        strokeWidth={isSelected ? 3 : 2}
        cornerRadius={2}
      />
      {/* Número del asiento */}
      <Text
        text={asiento.numero}
        fontSize={10}
        fontStyle="bold"
        fill={color}
        align="center"
        verticalAlign="middle"
        width={asiento.width}
        height={asiento.height}
      />
    </Group>
  );
};

// Componente para elementos especiales
const ElementoEspecialComponent = ({ elemento }) => {
  let color = "#666666";
  switch (elemento.tipo) {
    case "escalera":
      color = "#95A5A6";
      break;
    case "ingreso":
      color = "#27AE60";
      break;
    case "salida_emergencia":
      color = "#E74C3C";
      break;
    case "baño":
      color = "#3498DB";
      break;
    case "pasillo":
      color = "#BDC3C7";
      break;
    default:
      color = "#7F8C8D";
  }

  const texto = elemento.etiqueta || elemento.tipo.replace('_', ' ');

  return (
    <Group x={elemento.x} y={elemento.y}>
      <Rect
        width={elemento.width}
        height={elemento.height}
        fill={color}
        stroke="#34495E"
        strokeWidth={2}
      />
      <Text
        text={texto.toUpperCase()}
        fontSize={Math.min(elemento.height * 0.4, 16)}
        fontStyle="bold"
        fill="#FFFFFF"
        align="center"
        verticalAlign="middle"
        width={elemento.width}
        height={elemento.height}
      />
    </Group>
  );
};

// Componente para la imagen de fondo
const BackgroundImage = ({ imagenFondo, opacidad, width, height }) => {
  const [image, setImage] = useState(null);

  useEffect(() => {
    if (imagenFondo) {
      const img = new window.Image();
      img.onload = () => {
        if (img.width > 0 && img.height > 0) {
          setImage(img);
        } else {
          setImage(null);
        }
      };
      img.onerror = () => {
        setImage(null);
      };
      img.src = imagenFondo;
    } else {
      setImage(null);
    }
  }, [imagenFondo]);

  if (!image || !width || !height || width === 0 || height === 0) {
    return null;
  }

  return (
    <KonvaImage
      image={image}
      width={width}
      height={height}
      opacity={opacidad}
    />
  );
};

const SiteMapViewer = ({ fechaSeleccionada, siteMap, onAsientoSelect, asientosSeleccionados = [], entradas = [], reportes = [] }) => {
  const stageRef = useRef(null);
  const [asientosVendidos, setAsientosVendidos] = useState(new Set());
  const [zoom, setZoom] = useState(0.5);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });

  // Calcular dimensiones del canvas
  const canvasWidth = siteMap?.workspaceGridColumns
    ? siteMap.workspaceGridColumns * GRID_SIZE
    : 1120;
  const canvasHeight = siteMap?.workspaceGridRows
    ? siteMap.workspaceGridRows * GRID_SIZE
    : 700;

  // Procesar reportes para identificar asientos vendidos
  useEffect(() => {
    console.log("=== Procesando reportes para asientos vendidos ===");
    console.log("Reportes recibidos:", reportes);
    console.log("Fecha seleccionada:", fechaSeleccionada);
    console.log("SiteMap asientos:", siteMap?.asientos);

    if (!reportes || reportes.length === 0 || !siteMap || !siteMap.asientos) {
      setAsientosVendidos(new Set());
      return;
    }

    const vendidos = new Set();

    reportes.forEach((reporte, reporteIdx) => {
      console.log(`\n--- Procesando reporte ${reporteIdx} ---`);
      console.log("Reporte:", reporte);
      console.log("Fecha del reporte:", reporte.fecha);
      console.log("¿Coincide fecha?:", reporte.fecha == fechaSeleccionada);

      // Solo procesar reportes de la fecha seleccionada
      if (reporte.fecha !== fechaSeleccionada) {
        console.log("⏭️ Saltando reporte - fecha no coincide");
        return;
      }

      let codigos = reporte.reporte_codigo;

      // Si viene como string JSON, parsearlo
      if (typeof codigos === 'string') {
        try {
          codigos = JSON.parse(codigos);
        } catch (e) {
          console.error("Error al parsear reporte_codigo:", e);
          return;
        }
      }

      if (codigos && Array.isArray(codigos)) {
        console.log(`Códigos en reporte: ${codigos.length}`);

        codigos.forEach((codigo, codigoIdx) => {
          console.log(`  Código[${codigoIdx}]:`, codigo);
          console.log(`  sector_numero:`, codigo.sector_numero);

          if (!codigo.sector_numero) {
            console.log(`  ⚠️ sector_numero vacío`);
            return;
          }

          // Extraer información del sector_numero
          // Formatos posibles:
          // - "Sector A - Asiento 10"
          // - "Sector A -"
          // - "123" (ID directo)
          // - "Fila A - Asiento 10"
          const sectorNumeroStr = String(codigo.sector_numero).trim();

          let asientoEncontrado = null;

          // Método 1: Intentar extraer número del formato "Asiento X" o "Asiento: X"
          const matchAsiento = sectorNumeroStr.match(/Asiento[:\s]+(\d+)/i);
          if (matchAsiento) {
            const numeroExtraido = matchAsiento[1];
            asientoEncontrado = siteMap.asientos.find(asiento => asiento.numero == numeroExtraido);

            if (asientoEncontrado) {
              console.log(`    ✅ Match por patrón "Asiento X": ${asientoEncontrado.numero} (ID: ${asientoEncontrado.id})`);
            }
          }

          // Método 2: Buscar por texto completo en asiento.numero (si no se encontró)
          if (!asientoEncontrado) {
            asientoEncontrado = siteMap.asientos.find(asiento => {
              const numeroAsiento = String(asiento.numero || '').trim();
              if (numeroAsiento && sectorNumeroStr.includes(numeroAsiento)) {
                console.log(`    ✅ Match por inclusión de texto: "${numeroAsiento}" en "${sectorNumeroStr}"`);
                return true;
              }
              return false;
            });
          }

          // Método 3: Buscar por fila + número si existe el formato
          if (!asientoEncontrado) {
            const matchFila = sectorNumeroStr.match(/Fila[:\s]+([A-Z0-9]+)[^0-9]*(\d+)/i);
            if (matchFila) {
              const fila = matchFila[1];
              const numero = matchFila[2];
              asientoEncontrado = siteMap.asientos.find(asiento =>
                asiento.fila == fila && asiento.numero == numero
              );

              if (asientoEncontrado) {
                console.log(`    ✅ Match por fila+número: Fila ${fila}, Asiento ${numero} (ID: ${asientoEncontrado.id})`);
              }
            }
          }

          // Método 4: Intentar interpretar sector_numero como ID directo
          if (!asientoEncontrado) {
            const posibleId = parseInt(sectorNumeroStr);
            if (!isNaN(posibleId)) {
              asientoEncontrado = siteMap.asientos.find(a => a.id == posibleId);
              if (asientoEncontrado) {
                console.log(`    ✅ Match por ID directo: ${asientoEncontrado.id}`);
              }
            }
          }

          // Registrar asiento como vendido
          if (asientoEncontrado) {
            vendidos.add(asientoEncontrado.id);
            console.log(`  ✅ Asiento marcado como vendido - ID: ${asientoEncontrado.id}, Fila: ${asientoEncontrado.fila || 'N/A'}, Número: ${asientoEncontrado.numero}`);
          } else {
            console.log(`  ❌ No se encontró asiento para: "${codigo.sector_numero}"`);
            console.log(`    Asientos disponibles (primeros 5):`, siteMap.asientos.slice(0, 5).map(a => ({
              id: a.id,
              fila: a.fila,
              numero: a.numero,
              sector_id: a.sector_id
            })));
          }
        });
      }
    });

    console.log(`\n=== RESUMEN ===`);
    console.log(`Total asientos vendidos: ${vendidos.size}`);
    console.log("IDs de asientos vendidos:", Array.from(vendidos));
    setAsientosVendidos(vendidos);
  }, [reportes, fechaSeleccionada, siteMap]);

  // Manejar zoom con rueda del mouse
  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.1;
    const stage = stageRef.current;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    const clampedScale = Math.max(0.1, Math.min(3, newScale));

    setZoom(clampedScale);

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    setStagePos(newPos);
  };

  const handleDragEnd = (e) => {
    setStagePos({
      x: e.target.x(),
      y: e.target.y(),
    });
  };

  // Manejar click en asiento
  const handleAsientoClick = (asiento) => {
    console.log("Asiento clickeado:", asiento);
    console.log("Sitemap:", siteMap);

    if (!siteMap || !siteMap.sectores) {
      console.log("No hay sitemap o sectores");
      return;
    }

    // Buscar el sector del asiento en el sitemap
    const sectorDelAsiento = siteMap.sectores.find(s => s.id == asiento.sector_id);
    console.log("Sector del asiento:", sectorDelAsiento);

    // Verificar si el sector del asiento pertenece a alguna entrada activa
    const entradaDelSector = entradas.find(entrada => {
      console.log("  Verificando entrada:", entrada.nombre, "sector:", entrada.sector);

      // Extraer el nombre del sector de la entrada
      let nombreSectorEntrada = '';
      if (typeof entrada.sector === 'string' && entrada.sector.trim() !== "") {
        nombreSectorEntrada = entrada.sector.trim();
      } else if (entrada.sector && entrada.sector[0] && entrada.sector[0].trim() !== "") {
        nombreSectorEntrada = entrada.sector[0].trim();
      } else if (Array.isArray(entrada.sector) && entrada.sector.length > 0) {
        nombreSectorEntrada = entrada.sector[0] ? String(entrada.sector[0]).trim() : '';
      }

      // Opción 1: Si la entrada tiene sector definido, comparar con el nombre del sector del mapa
      if (nombreSectorEntrada !== '' && sectorDelAsiento) {
        const nombreSectorMapa = sectorDelAsiento.nombre || sectorDelAsiento.name || '';
        const match = nombreSectorMapa.trim().toLowerCase() === nombreSectorEntrada.toLowerCase();

        if (match) {
          console.log(`✅ Match por campo sector: "${nombreSectorMapa}" = entrada.sector "${nombreSectorEntrada}"`);
          return true;
        }
      }

      // Opción 2: Fallback - Verificar por nombre de entrada (si no tiene sector definido)
      if (nombreSectorEntrada === '' && sectorDelAsiento) {
        const nombreSectorMapa = sectorDelAsiento.nombre || sectorDelAsiento.name || '';
        const nombreEntrada = entrada.nombre || '';

        const matchPorNombre = nombreSectorMapa.trim().toLowerCase() === nombreEntrada.trim().toLowerCase();

        if (matchPorNombre) {
          console.log(`✅ Match por nombre de entrada: Sector "${nombreSectorMapa}" = Entrada "${nombreEntrada}"`);
          return true;
        }
      }

      return false;
    });

    if (entradaDelSector && onAsientoSelect) {
      console.log("✅ Entrada del sector encontrada:", entradaDelSector);
      onAsientoSelect(asiento);
    } else {
      console.log("❌ No se encontró entrada para el sector_id:", asiento.sector_id);
    }
  };

  if (!siteMap) {
    return <div className="text-center text-muted">No hay mapa disponible</div>;
  }

  return (
    <div className="border rounded bg-white p-2 shadow-sm">
      {/* Controles */}
      <div className="mb-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="fw-bold text-dark">
            <i className="bi bi-hand-index me-1"></i>
            Selecciona tus asientos
          </small>
          <div className="d-flex gap-1">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setZoom(Math.min(3, zoom + 0.1))}
              title="Acercar zoom"
            >
              <i className="bi bi-zoom-in"></i>
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setZoom(Math.max(0.1, zoom - 0.1))}
              title="Alejar zoom"
            >
              <i className="bi bi-zoom-out"></i>
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                setZoom(0.5);
                setStagePos({ x: 0, y: 0 });
              }}
              title="Resetear vista"
            >
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="text-muted" style={{fontSize: '10px'}}>
            <i className="bi bi-info-circle me-1"></i>
            Usa la rueda del mouse para zoom. Arrastra para mover.
          </small>
          <small className="badge bg-secondary" style={{fontSize: '9px'}}>
            Zoom: {(zoom * 100).toFixed(0)}%
          </small>
        </div>

        {/* Leyenda */}
        <div className="d-flex flex-wrap gap-2 mb-2">
          <small className="d-flex align-items-center">
            <span className="badge rounded-circle me-1" style={{backgroundColor: "#5B9BD5", width: "12px", height: "12px"}}></span>
            Disponible
          </small>
          <small className="d-flex align-items-center">
            <span className="badge rounded-circle me-1" style={{backgroundColor: "#28A745", width: "12px", height: "12px"}}></span>
            Seleccionado
          </small>
          <small className="d-flex align-items-center">
            <span className="badge rounded-circle me-1" style={{backgroundColor: "#DC3545", width: "12px", height: "12px"}}></span>
            Vendido
          </small>
        </div>
      </div>

      {/* Canvas del mapa */}
      <div
        className="border rounded"
        style={{
          backgroundColor: "#ffffff",
          overflow: "hidden",
          boxShadow: "inset 0 0 10px rgba(0,0,0,0.1)"
        }}
      >
        <Stage
          ref={stageRef}
          width={canvasWidth}
          height={canvasHeight}
          scaleX={zoom}
          scaleY={zoom}
          x={stagePos.x}
          y={stagePos.y}
          onWheel={handleWheel}
          draggable={true}
          onDragEnd={handleDragEnd}
          style={{ cursor: 'grab' }}
        >
          <Layer>
            {/* Grid de fondo */}
            {Array.from({ length: Math.ceil(5000 / GRID_SIZE) }).map((_, i) => (
              <React.Fragment key={`grid-${i}`}>
                <Rect
                  x={i * GRID_SIZE}
                  y={0}
                  width={1}
                  height={5000}
                  fill="#f0f0f0"
                />
                <Rect
                  x={0}
                  y={i * GRID_SIZE}
                  width={5000}
                  height={1}
                  fill="#f0f0f0"
                />
              </React.Fragment>
            ))}

            {/* Imagen de fondo */}
            {siteMap.imagenFondo && (
              <BackgroundImage
                imagenFondo={siteMap.imagenFondo}
                opacidad={siteMap.opacidadFondo || 0.3}
                width={canvasWidth}
                height={canvasHeight}
              />
            )}

            {/* Escenario */}
            {siteMap.escenario && <EscenarioComponent escenario={siteMap.escenario} />}

            {/* Elementos especiales */}
            {siteMap.elementosEspeciales && siteMap.elementosEspeciales.map((elemento) => (
              <ElementoEspecialComponent
                key={elemento.id}
                elemento={elemento}
              />
            ))}

            {/* Etiquetas de sectores para mapas virtuales */}
            {siteMap.isVirtual && siteMap.sectores && siteMap.sectores.map((sector, idx) => {
              // Encontrar el primer asiento del sector para posicionar la etiqueta
              const primerAsiento = siteMap.asientos.find(a => a.sector_id === sector.id);
              if (!primerAsiento) return null;

              return (
                <Group key={`label-${sector.id}`} x={primerAsiento.x} y={primerAsiento.y - 45}>
                  <Rect
                    width={300}
                    height={38}
                    fill={sector.color}
                    opacity={0.95}
                    cornerRadius={6}
                    shadowBlur={5}
                    shadowColor="rgba(0,0,0,0.3)"
                  />
                  <Text
                    text={`${sector.nombre} (${sector.capacidad} asientos)`}
                    fontSize={20}
                    fontStyle="bold"
                    fill="#FFFFFF"
                    align="left"
                    verticalAlign="middle"
                    width={300}
                    height={38}
                    padding={8}
                  />
                </Group>
              );
            })}

            {/* Asientos */}
            {siteMap.asientos && siteMap.asientos.map((asiento) => {
              const sector = siteMap.sectores ? siteMap.sectores.find((s) => s.id == asiento.sector_id) : null;
              const isSelected = asientosSeleccionados.includes(asiento.id);
              const asientoIdNum = typeof asiento.id === 'string' ? parseInt(asiento.id) : asiento.id;
              const isVendido = asientosVendidos.has(asiento.id) || asientosVendidos.has(asientoIdNum) || asientosVendidos.has(String(asiento.id));

              return (
                <AsientoComponent
                  key={asiento.id}
                  asiento={asiento}
                  sector={sector}
                  isSelected={isSelected}
                  isVendido=  {isVendido}
                  onClick={handleAsientoClick}
                />
              );
            })}
          </Layer>
        </Stage>
      </div>
    </div>
  );
};

export default SiteMapViewer;
